import React, { useState, useEffect } from "react"
import {
	MessageSquare,
	BookOpen,
	Layers,
	ChevronLeft,
	GraduationCap,
	Filter,
	TrendingUp,
	Brain,
	Check,
	Play,
	Volume2,
} from "lucide-react"
import { collection, getDocs, query, where, orderBy } from "firebase/firestore"
import { db } from "../firebase/firebase"
import SpanishGrammarPage from "./SpanishGrammarPage"

const LearnPage = ({
	currentLanguage,
	currentFlashcards,
	handleFlashcardSelect,
	handleTabClick,
	knownWords = new Set(),
	knownWordsCount = 0,
	onMarkAsKnown,
}) => {
	const [isLoading, setIsLoading] = useState(true)
	const [activeSection, setActiveSection] = useState(null)

	// Conversation states
	const [selectedLevel, setSelectedLevel] = useState(null)
	const [selectedLesson, setSelectedLesson] = useState(null)
	const [selectedConversation, setSelectedConversation] = useState(null)
	const [conversationLessons, setConversationLessons] = useState({
		beginner: [],
		intermediate: [],
		advanced: [],
	})
	const [currentDialogueIndex, setCurrentDialogueIndex] = useState(0)
	const [showTranslation, setShowTranslation] = useState(false)

	// Flashcard states
	const [flashcardFilter, setFlashcardFilter] = useState("all")
	const [selectedCategory, setSelectedCategory] = useState("all")

	// Handle section selection
	const handleSectionSelect = section => {
		setActiveSection(section)
		setSelectedLevel(null)
		setSelectedLesson(null)
		setSelectedConversation(null)
	}

	// Fetch conversation lessons when language changes
	useEffect(() => {
		const fetchLessons = async () => {
			if (currentLanguage?.code === "es") {
				setIsLoading(true)
				try {
					const levels = ["beginner", "intermediate", "advanced"]
					const lessonsData = {}

					for (const level of levels) {
						const q = query(
							collection(db, "conversationLessons"),
							where("language", "==", "es"),
							where("level", "==", level)
						)
						const snapshot = await getDocs(q)
						lessonsData[level] = snapshot.docs.map(doc => ({
							id: doc.id,
							...doc.data(),
							conversations: [],
						}))
					}

					setConversationLessons(lessonsData)
				} catch (error) {
					console.error("Error fetching conversation lessons:", error)
				} finally {
					setIsLoading(false)
				}
			} else {
				setIsLoading(false)
			}
		}

		fetchLessons()
	}, [currentLanguage])

	// Fetch conversations for a specific lesson
	const fetchConversations = async lessonId => {
		try {
			const q = query(
				collection(db, "conversationLessons", lessonId, "conversations"),
				orderBy("order", "asc")
			)
			const snapshot = await getDocs(q)
			return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
		} catch (error) {
			console.error(
				`Error fetching conversations for lesson ${lessonId}:`,
				error
			)
			return []
		}
	}

	const handleLessonClick = async lesson => {
		setIsLoading(true)
		try {
			const conversations = await fetchConversations(lesson.id)
			setSelectedLesson({ ...lesson, conversations })
		} catch (error) {
			console.error("Error loading lesson:", error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleConversationClick = conversation => {
		setSelectedConversation(conversation)
		setCurrentDialogueIndex(0)
		setShowTranslation(false)
	}

	// Conversation navigation helpers
	const toggleTranslation = () => setShowTranslation(!showTranslation)
	const nextDialogue = () => {
		if (currentDialogueIndex < selectedConversation.dialogues.length - 1) {
			setCurrentDialogueIndex(currentDialogueIndex + 1)
			setShowTranslation(false)
		}
	}
	const prevDialogue = () => {
		if (currentDialogueIndex > 0) {
			setCurrentDialogueIndex(currentDialogueIndex - 1)
			setShowTranslation(false)
		}
	}

	const speakText = (text, lang = "es-ES") => {
		if ("speechSynthesis" in window) {
			const utterance = new SpeechSynthesisUtterance(text)
			utterance.lang = lang
			speechSynthesis.speak(utterance)
		}
	}

	// Flashcard filtering functions
	const getUniqueCategories = () => {
		return [...new Set(currentFlashcards.map(card => card.category))]
	}

	const getFilteredFlashcards = () => {
		let filtered = currentFlashcards

		if (flashcardFilter === "unknown") {
			filtered = filtered.filter(card => !knownWords.has(card.id))
		} else if (flashcardFilter === "known") {
			filtered = filtered.filter(card => knownWords.has(card.id))
		}

		if (selectedCategory !== "all") {
			filtered = filtered.filter(card => card.category === selectedCategory)
		}

		return filtered.sort((a, b) => {
			const aKnown = knownWords.has(a.id)
			const bKnown = knownWords.has(b.id)
			if (aKnown === bKnown) return 0
			return aKnown ? 1 : -1
		})
	}

	const getFilterCount = type => {
		if (type === "unknown") {
			return currentFlashcards.filter(card => !knownWords.has(card.id)).length
		} else if (type === "known") {
			return knownWordsCount
		}
		return currentFlashcards.length
	}

	if (isLoading) {
		return (
			<div className='flex justify-center items-center h-64'>
				<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3A6E]'></div>
			</div>
		)
	}

	// Enhanced Flashcards section
	if (activeSection === "flashcards") {
		const filteredFlashcards = getFilteredFlashcards()
		const categories = getUniqueCategories()
		const totalFlashcards = currentFlashcards.length
		const progressPercentage =
			totalFlashcards > 0
				? Math.round((knownWordsCount / totalFlashcards) * 100)
				: 0

		return (
			<div>
				<div className='flex items-center mb-6'>
					<button
						className='bg-[#1E1E1E] text-white p-2 rounded-lg mr-3'
						onClick={() => setActiveSection(null)}>
						<ChevronLeft size={20} />
					</button>
					<h1 className='text-2xl font-bold text-white'>
						{currentLanguage.name} Flashcards
					</h1>
				</div>

				{/* Progress Overview */}
				<div className='bg-[#1E1E1E] rounded-lg p-6 mb-6'>
					<div className='flex items-center justify-between mb-4'>
						<h3 className='text-white font-semibold flex items-center'>
							<TrendingUp size={20} className='mr-2 text-[#FF3A6E]' />
							Learning Progress
						</h3>
						<div className='text-[#FF3A6E] font-bold text-lg'>
							{progressPercentage}%
						</div>
					</div>

					<div className='mb-4'>
						<div className='bg-[#2C2C2C] rounded-full h-3 overflow-hidden'>
							<div
								className='bg-gradient-to-r from-[#FF3A6E] to-[#FF6D4B] h-full transition-all duration-500'
								style={{ width: `${progressPercentage}%` }}></div>
						</div>
					</div>

					<div className='grid grid-cols-3 gap-4 text-center'>
						<div className='bg-[#2C2C2C] rounded-lg p-3'>
							<div className='text-white text-2xl font-bold'>
								{knownWordsCount}
							</div>
							<div className='text-[#A0A0A0] text-sm'>Known Words</div>
						</div>
						<div className='bg-[#2C2C2C] rounded-lg p-3'>
							<div className='text-white text-2xl font-bold'>
								{getFilterCount("unknown")}
							</div>
							<div className='text-[#A0A0A0] text-sm'>To Learn</div>
						</div>
						<div className='bg-[#2C2C2C] rounded-lg p-3'>
							<div className='text-white text-2xl font-bold'>
								{totalFlashcards}
							</div>
							<div className='text-[#A0A0A0] text-sm'>Total Cards</div>
						</div>
					</div>
				</div>

				{/* Filter Controls */}
				<div className='bg-[#1E1E1E] rounded-lg p-4 mb-6'>
					<div className='flex items-center gap-2 mb-4'>
						<Filter size={16} className='text-[#FF3A6E]' />
						<span className='text-white font-medium'>Filter Cards</span>
					</div>

					<div className='flex flex-wrap gap-2 mb-4'>
						{[
							{
								id: "all",
								label: `All Cards (${totalFlashcards})`,
								color: "bg-[#FF3A6E]",
							},
							{
								id: "unknown",
								label: `Need Practice (${getFilterCount("unknown")})`,
								color: "bg-orange-600",
								icon: Brain,
							},
							{
								id: "known",
								label: `Known (${knownWordsCount})`,
								color: "bg-green-600",
								icon: Check,
							},
						].map(filter => (
							<button
								key={filter.id}
								className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center ${
									flashcardFilter === filter.id
										? `${filter.color} text-white`
										: "bg-[#2C2C2C] text-[#A0A0A0] hover:bg-[#3C3C3C]"
								}`}
								onClick={() => setFlashcardFilter(filter.id)}>
								{filter.icon && <filter.icon size={14} className='mr-1' />}
								{filter.label}
							</button>
						))}
					</div>

					{categories.length > 1 && (
						<div>
							<label className='text-[#A0A0A0] text-sm mb-2 block'>
								Category:
							</label>
							<select
								className='bg-[#2C2C2C] text-white rounded-lg px-3 py-2 border-none outline-none'
								value={selectedCategory}
								onChange={e => setSelectedCategory(e.target.value)}>
								<option value='all'>All Categories</option>
								{categories.map(category => (
									<option key={category} value={category}>
										{category}
									</option>
								))}
							</select>
						</div>
					)}
				</div>

				{/* Flashcards Grid */}
				<div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
					{filteredFlashcards.length > 0 ? (
						filteredFlashcards.map(card => {
							const isKnown = knownWords.has(card.id)
							return (
								<div
									key={card.id}
									className={`relative p-4 rounded-lg cursor-pointer transition-all hover:scale-105 group ${
										isKnown
											? "bg-gradient-to-br from-green-900/30 to-green-800/30 border border-green-600/30"
											: "bg-[#1E1E1E] hover:bg-[#2C2C2C] border border-transparent"
									}`}
									onClick={() => handleFlashcardSelect(card)}>
									{isKnown && (
										<div className='absolute top-2 right-2 bg-green-600 text-white rounded-full p-1'>
											<Check size={12} />
										</div>
									)}
									<div className='text-white font-medium mb-2 group-hover:text-[#FF3A6E] transition-colors'>
										{card.front}
									</div>
									<div className='text-[#A0A0A0] text-sm mb-2'>
										{card.category}
									</div>
									<div className='flex items-center justify-between text-xs'>
										<span
											className={`px-2 py-1 rounded-full ${
												isKnown
													? "bg-green-600/20 text-green-400"
													: "bg-orange-600/20 text-orange-400"
											}`}>
											{isKnown ? "Known" : "Learning"}
										</span>
										<Play
											size={14}
											className='text-[#FF3A6E] opacity-0 group-hover:opacity-100 transition-opacity'
										/>
									</div>
								</div>
							)
						})
					) : (
						<div className='col-span-3 text-center py-10'>
							<div className='text-[#6E6E6E] mb-4'>
								{totalFlashcards === 0
									? `No flashcards available for ${currentLanguage.name} yet`
									: flashcardFilter === "known"
									? "No known words yet. Keep practicing!"
									: flashcardFilter === "unknown"
									? "Great job! You've learned all the words in this filter."
									: `No cards in "${selectedCategory}" category.`}
							</div>
							<div className='flex justify-center gap-3'>
								{flashcardFilter !== "all" && (
									<button
										className='bg-[#2C2C2C] text-white py-2 px-4 rounded-lg hover:bg-[#3C3C3C]'
										onClick={() => setFlashcardFilter("all")}>
										Show All Cards
									</button>
								)}
								{selectedCategory !== "all" && (
									<button
										className='bg-[#2C2C2C] text-white py-2 px-4 rounded-lg hover:bg-[#3C3C3C]'
										onClick={() => setSelectedCategory("all")}>
										All Categories
									</button>
								)}
							</div>
						</div>
					)}
				</div>

				{filteredFlashcards.length > 0 && (
					<div className='bg-[#1E1E1E] rounded-lg p-4 mt-6'>
						<h3 className='text-white font-semibold mb-3'>💡 Study Tips</h3>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm'>
							{[
								"Focus on 'Need Practice' cards to improve faster",
								"Review known words occasionally to maintain retention",
								"Try to think of the answer before flipping the card",
								"Study consistently for better long-term retention",
							].map((tip, i) => (
								<div key={i} className='flex items-start text-[#A0A0A0]'>
									<span className='text-[#FF3A6E] mr-2'>•</span>
									<span>{tip}</span>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		)
	}

	// Enhanced Grammar section
	if (activeSection === "grammar") {
		return <SpanishGrammarPage onBackToHome={() => setActiveSection(null)} />
	}

	// Enhanced Conversations section with dialogue navigation
	if (selectedConversation) {
		const currentDialogue = selectedConversation.dialogues[currentDialogueIndex]
		const progress =
			((currentDialogueIndex + 1) / selectedConversation.dialogues.length) * 100

		return (
			<div>
				<button
					className='bg-[#1E1E1E] text-white py-2 px-4 rounded-lg flex items-center mb-4'
					onClick={() => setSelectedConversation(null)}>
					<ChevronLeft size={16} className='mr-2' />
					Back to Conversations
				</button>

				<div className='bg-[#1E1E1E] rounded-lg p-6'>
					<div className='flex justify-between items-center mb-4'>
						<h2 className='text-xl font-bold text-white'>
							{selectedConversation.title}
						</h2>
						<div className='text-[#A0A0A0] text-sm'>
							{currentDialogueIndex + 1} /{" "}
							{selectedConversation.dialogues.length}
						</div>
					</div>

					{/* Progress bar */}
					<div className='mb-6'>
						<div className='w-full bg-[#2C2C2C] rounded-full h-2'>
							<div
								className='bg-[#FF3A6E] h-2 rounded-full transition-all duration-300'
								style={{ width: `${progress}%` }}></div>
						</div>
					</div>

					{/* Current dialogue */}
					<div className='bg-[#2C2C2C] rounded-lg p-6 mb-6'>
						<div className='flex items-center mb-4'>
							<div className='bg-[#FF3A6E] h-10 w-10 rounded-full flex items-center justify-center mr-3'>
								<span className='text-white font-medium'>
									{currentDialogue.speaker.charAt(0)}
								</span>
							</div>
							<span className='text-white font-medium text-lg'>
								{currentDialogue.speaker}
							</span>
							<button
								className='ml-auto bg-[#3C3C3C] p-2 rounded-lg hover:bg-[#4C4C4C]'
								onClick={() => speakText(currentDialogue.text)}>
								<Volume2 size={16} className='text-white' />
							</button>
						</div>

						<div className='bg-[#1E1E1E] p-4 rounded-lg mb-3'>
							<p className='text-white text-xl'>{currentDialogue.text}</p>
						</div>

						{showTranslation && (
							<div className='bg-[#3C3C3C] p-4 rounded-lg mb-3'>
								<p className='text-[#A0A0A0]'>{currentDialogue.translation}</p>
							</div>
						)}

						<div className='flex justify-between items-center'>
							<button
								className='bg-[#3C3C3C] text-white py-2 px-4 rounded-lg hover:bg-[#4C4C4C]'
								onClick={toggleTranslation}>
								{showTranslation ? "Hide Translation" : "Show Translation"}
							</button>

							<div className='flex space-x-2'>
								<button
									className='bg-[#2C2C2C] text-white py-2 px-4 rounded-lg disabled:opacity-50'
									onClick={prevDialogue}
									disabled={currentDialogueIndex === 0}>
									Previous
								</button>
								<button
									className='bg-[#FF3A6E] text-white py-2 px-4 rounded-lg disabled:opacity-50'
									onClick={nextDialogue}
									disabled={
										currentDialogueIndex ===
										selectedConversation.dialogues.length - 1
									}>
									Next
								</button>
							</div>
						</div>
					</div>

					{/* Vocabulary section */}
					{selectedConversation.vocabulary && (
						<div className='mb-6'>
							<h3 className='text-white font-semibold mb-3'>Key Vocabulary</h3>
							<div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
								{selectedConversation.vocabulary.map((item, index) => (
									<div
										key={index}
										className='bg-[#2C2C2C] p-3 rounded-lg hover:bg-[#3C3C3C] transition-colors'>
										<div className='flex justify-between items-center'>
											<span className='text-white font-medium'>
												{item.word}
											</span>
											<button
												onClick={() => speakText(item.word)}
												className='text-[#FF3A6E] hover:text-[#FF6D4B]'>
												<Volume2 size={14} />
											</button>
										</div>
										<div className='text-[#FF3A6E] text-sm'>
											{item.translation}
										</div>
										<div className='text-[#A0A0A0] text-xs mt-1'>
											{item.example}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Practice Activities */}
					{selectedConversation.practice && (
						<div>
							<h3 className='text-white font-semibold mb-3'>
								Practice Activities
							</h3>
							{selectedConversation.practice.map((activity, index) => (
								<div
									key={index}
									className='bg-[#2C2C2C] p-4 rounded-lg mb-3 last:mb-0'>
									<h4 className='text-white font-medium mb-2'>
										{activity.type &&
											activity.type
												.replace(/-/g, " ")
												.replace(/\b\w/g, l => l.toUpperCase())}
									</h4>
									<p className='text-[#A0A0A0] mb-3'>
										{activity.instructions || activity.question}
									</p>

									{activity.type === "role-play" && activity.keyphrases && (
										<div className='bg-[#3C3C3C] p-3 rounded-lg mb-3'>
											<p className='text-white text-sm mb-2'>
												Scenario: {activity.scenario}
											</p>
											<div className='space-y-1'>
												{activity.keyphrases.map((phrase, i) => (
													<div
														key={i}
														className='flex items-center justify-between bg-[#2C2C2C] p-2 rounded text-sm'>
														<span className='text-[#A0A0A0]'>{phrase}</span>
														<button
															onClick={() => speakText(phrase)}
															className='text-[#FF3A6E]'>
															<Volume2 size={12} />
														</button>
													</div>
												))}
											</div>
										</div>
									)}

									{activity.type === "matching" && activity.pairs && (
										<div className='grid grid-cols-2 gap-2 mb-3'>
											{activity.pairs.map((pair, i) => (
												<React.Fragment key={i}>
													<div className='bg-[#3C3C3C] p-2 rounded-lg text-white text-sm'>
														{pair.spanish}
													</div>
													<div className='bg-[#3C3C3C] p-2 rounded-lg text-[#A0A0A0] text-sm'>
														{pair.english}
													</div>
												</React.Fragment>
											))}
										</div>
									)}

									{activity.type === "fill-in-blank" && activity.options && (
										<div className='bg-[#3C3C3C] p-3 rounded-lg'>
											<p className='text-white mb-2'>{activity.question}</p>
											<div className='grid grid-cols-2 gap-2'>
												{activity.options.map((option, i) => (
													<div
														key={i}
														className={`p-2 rounded-lg text-center ${
															option === activity.answer
																? "bg-[#FF3A6E] text-white"
																: "bg-[#2C2C2C] text-[#A0A0A0]"
														}`}>
														{option}
													</div>
												))}
											</div>
										</div>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		)
	}

	// Lesson detail view
	if (selectedLesson) {
		return (
			<div>
				<button
					className='bg-[#1E1E1E] text-white py-2 px-4 rounded-lg flex items-center mb-4'
					onClick={() => setSelectedLesson(null)}>
					<ChevronLeft size={16} className='mr-2' />
					Back to Lessons
				</button>

				<div className='bg-[#1E1E1E] rounded-lg p-6 mb-6'>
					<h2 className='text-xl font-bold text-white mb-2'>
						{selectedLesson.title}
					</h2>
					<p className='text-[#A0A0A0] mb-4'>{selectedLesson.description}</p>
					{selectedLesson.topics && (
						<div className='flex flex-wrap gap-2'>
							{selectedLesson.topics.map((topic, i) => (
								<span
									key={i}
									className='text-xs px-2 py-1 bg-[#2C2C2C] text-[#A0A0A0] rounded-full'>
									{topic}
								</span>
							))}
						</div>
					)}
				</div>

				<h3 className='text-white font-semibold mb-3'>
					Available Conversations
				</h3>
				<div className='grid grid-cols-1 gap-4'>
					{selectedLesson.conversations?.length > 0 ? (
						selectedLesson.conversations.map(conversation => (
							<button
								key={conversation.id}
								className='bg-[#2C2C2C] p-4 rounded-lg text-left hover:bg-[#3C3C3C] transition-colors'
								onClick={() => handleConversationClick(conversation)}>
								<div className='flex justify-between items-start mb-2'>
									<h4 className='text-white font-medium'>
										{conversation.title}
									</h4>
									<div className='flex items-center text-[#FF3A6E] text-sm'>
										<MessageSquare size={16} className='mr-1' />
										<span>{conversation.dialogues?.length || 0}</span>
									</div>
								</div>
								<p className='text-[#A0A0A0] text-sm'>
									{conversation.description}
								</p>
							</button>
						))
					) : (
						<div className='text-center py-8 text-[#6E6E6E]'>
							No conversations available for this lesson.
						</div>
					)}
				</div>
			</div>
		)
	}

	// Level selection view
	if (selectedLevel) {
		const levelLessons = conversationLessons[selectedLevel] || []
		const levelTitle =
			selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1)

		return (
			<div>
				<div className='flex justify-between items-center mb-4'>
					<h2 className='text-xl font-semibold text-white'>
						{levelTitle} Level - Spanish Conversations
					</h2>
				</div>

				{levelLessons.length > 0 ? (
					<div className='grid grid-cols-1 gap-4 mb-6'>
						{levelLessons.map(lesson => (
							<button
								key={lesson.id}
								className='bg-[#2C2C2C] p-4 rounded-lg text-left hover:bg-[#3C3C3C] transition-colors'
								onClick={() => handleLessonClick(lesson)}>
								<div className='flex items-start'>
									<div className='bg-[#FF3A6E] h-10 w-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0'>
										<MessageSquare size={20} className='text-white' />
									</div>
									<div className='flex-1'>
										<h3 className='text-white font-medium'>{lesson.title}</h3>
										<p className='text-[#A0A0A0] text-sm'>
											{lesson.description}
										</p>
										{lesson.topics && (
											<div className='flex flex-wrap gap-2 mt-2'>
												{lesson.topics.slice(0, 3).map((topic, i) => (
													<span
														key={i}
														className='text-xs px-2 py-0.5 bg-[#3C3C3C] text-[#A0A0A0] rounded-full'>
														{topic}
													</span>
												))}
											</div>
										)}
									</div>
								</div>
							</button>
						))}
					</div>
				) : (
					<div className='text-center py-10 text-[#6E6E6E]'>
						No {levelTitle.toLowerCase()} conversation lessons available yet
					</div>
				)}

				<button
					className='bg-[#1E1E1E] text-white py-3 px-4 rounded-lg flex items-center'
					onClick={() => setSelectedLevel(null)}>
					<ChevronLeft size={16} className='mr-2' />
					Back to Levels
				</button>
			</div>
		)
	}

	// Conversations level selection
	if (activeSection === "conversations") {
		const levelConfigs = [
			{
				key: "beginner",
				title: "Beginner",
				desc: "Basic conversations for everyday situations",
				gradient: "from-[#55E6A5] to-[#00B4DB]",
			},
			{
				key: "intermediate",
				title: "Intermediate",
				desc: "More complex dialogues on various topics",
				gradient: "from-[#FF42A1] to-[#7B4AFF]",
			},
			{
				key: "advanced",
				title: "Advanced",
				desc: "Sophisticated discussions on culture and society",
				gradient: "from-[#FF4D4D] to-[#9B51E0]",
			},
		]

		return (
			<div>
				<div className='flex items-center mb-6'>
					<button
						className='bg-[#1E1E1E] text-white p-2 rounded-lg mr-3'
						onClick={() => setActiveSection(null)}>
						<ChevronLeft size={20} />
					</button>
					<h1 className='text-2xl font-bold text-white'>
						Spanish Conversations
					</h1>
				</div>

				<p className='text-[#A0A0A0] mb-6'>
					Practice Spanish with interactive dialogues at different levels.
				</p>

				<div className='grid grid-cols-1 gap-5 mb-6'>
					{levelConfigs.map(level => (
						<button
							key={level.key}
							className={`bg-gradient-to-r ${level.gradient} p-6 rounded-lg text-left hover:scale-105 transition-transform`}
							onClick={() => setSelectedLevel(level.key)}>
							<div className='flex justify-between items-center'>
								<div>
									<h2 className='text-white text-xl font-bold mb-2'>
										{level.title}
									</h2>
									<p className='text-white text-opacity-80'>{level.desc}</p>
								</div>
								<div className='bg-white bg-opacity-20 rounded-full px-3 py-1'>
									<span className='text-white'>
										{conversationLessons[level.key]?.length || 0}{" "}
										{conversationLessons[level.key]?.length === 1
											? "topic"
											: "topics"}
									</span>
								</div>
							</div>
						</button>
					))}
				</div>

				{/* Tips section */}
				<div className='bg-[#1E1E1E] rounded-lg p-4'>
					<h3 className='text-white font-semibold mb-2'>Practice Tips</h3>
					<div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm'>
						{[
							"Listen to pronunciation by clicking the audio buttons",
							"Try to understand Spanish before checking translations",
							"Practice speaking both sides of conversations aloud",
							"Focus on learning complete phrases, not just words",
						].map((tip, i) => (
							<div key={i} className='flex items-start text-[#A0A0A0]'>
								<span className='text-[#FF3A6E] mr-2'>•</span>
								<span>{tip}</span>
							</div>
						))}
					</div>
				</div>
			</div>
		)
	}

	// Main dashboard
	const totalConversations = Object.values(conversationLessons).reduce(
		(sum, lessons) => sum + lessons.length,
		0
	)
	const learningOptions = [
		{
			id: "grammar",
			title: "Grammar",
			description: `Master the rules and structure of ${currentLanguage.name}`,
			icon: BookOpen,
			gradient: "from-[#55E6A5] to-[#00B4DB]",
			stats: "Multiple levels",
			available: currentLanguage.code === "es",
		},
		{
			id: "flashcards",
			title: "Flashcards",
			description: `Build your ${currentLanguage.name} vocabulary`,
			icon: Layers,
			gradient: "from-[#FF42A1] to-[#7B4AFF]",
			stats: `${currentFlashcards.length} cards`,
			available: currentFlashcards.length > 0,
		},
		{
			id: "conversations",
			title: "Conversations",
			description: `Practice real-world dialogues in ${currentLanguage.name}`,
			icon: MessageSquare,
			gradient: "from-[#FF4D4D] to-[#9B51E0]",
			stats:
				currentLanguage.code === "es"
					? `${totalConversations} topics`
					: "Coming soon",
			available: currentLanguage.code === "es",
		},
	]

	return (
		<div className='py-6'>
			<div className='flex justify-between items-center mb-6'>
				<h1 className='text-2xl font-bold text-white'>
					Learn {currentLanguage.name}
				</h1>
			</div>

			<p className='text-[#A0A0A0] mb-8'>
				Choose your preferred learning method to improve your{" "}
				{currentLanguage.name} skills.
			</p>

			{/* Learning Options Grid */}
			<div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
				{learningOptions.map(option => (
					<div
						key={option.id}
						className={`relative overflow-hidden ${
							option.available
								? "cursor-pointer hover:scale-105"
								: "cursor-default opacity-75"
						} transition-transform`}
						onClick={
							option.available
								? () => handleSectionSelect(option.id)
								: undefined
						}>
						<div
							className={`bg-gradient-to-r ${option.gradient} rounded-lg p-6 h-full`}>
							{!option.available && (
								<div className='absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full'>
									Soon
								</div>
							)}

							<div className='bg-white bg-opacity-20 h-12 w-12 rounded-full flex items-center justify-center mb-4'>
								<option.icon className='text-white' size={24} />
							</div>

							<h2 className='text-white text-xl font-bold mb-2'>
								{option.title}
							</h2>
							<p className='text-white text-opacity-80 mb-4 text-sm'>
								{option.description}
							</p>

							<div className='flex justify-between items-center mt-auto'>
								<span className='text-white text-sm opacity-80'>
									{option.stats}
								</span>
								<div className='bg-white bg-opacity-20 rounded-full px-3 py-1'>
									<span className='text-white text-sm font-medium'>
										{option.available ? "Start" : "Soon"}
									</span>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>

			{/* Quick Stats */}
			<div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-8'>
				<div className='bg-[#1E1E1E] p-4 rounded-lg text-center'>
					<div className='text-[#FF3A6E] text-2xl font-bold'>
						{knownWordsCount}
					</div>
					<div className='text-[#A0A0A0] text-sm'>Words Learned</div>
				</div>
				<div className='bg-[#1E1E1E] p-4 rounded-lg text-center'>
					<div className='text-[#55E6A5] text-2xl font-bold'>45</div>
					<div className='text-[#A0A0A0] text-sm'>Minutes Today</div>
				</div>
				<div className='bg-[#1E1E1E] p-4 rounded-lg text-center'>
					<div className='text-[#FF42A1] text-2xl font-bold'>3</div>
					<div className='text-[#A0A0A0] text-sm'>Lessons Done</div>
				</div>
				<div className='bg-[#1E1E1E] p-4 rounded-lg text-center'>
					<div className='text-[#7B4AFF] text-2xl font-bold'>87%</div>
					<div className='text-[#A0A0A0] text-sm'>Accuracy</div>
				</div>
			</div>

			{/* Learning Tips */}
			<div className='bg-[#1E1E1E] rounded-lg p-6'>
				<h3 className='text-white font-semibold mb-4'>Learning Tips</h3>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					{[
						{
							icon: "🎯",
							tip: "Set daily goals and stick to them for consistent progress",
						},
						{
							icon: "🔄",
							tip: "Mix different learning methods to reinforce memory",
						},
						{
							icon: "📅",
							tip: "Practice a little each day rather than cramming",
						},
						{
							icon: "🗣️",
							tip: "Practice speaking aloud to improve pronunciation",
						},
					].map((item, i) => (
						<div key={i} className='flex items-start space-x-3'>
							<span className='text-xl'>{item.icon}</span>
							<span className='text-[#A0A0A0] text-sm'>{item.tip}</span>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

export default LearnPage
